// Origin-bound copy of a managed wallet seed. Trading signs from this, not
// from the recovery-wrapped account vault. Recovery remains account restore.

import { addressToFriendlyForm, addressToRawForm } from './tonRawAddress'

const STORE_KEY = 'wils-device-wallet-seeds-v1'

type SeedRecord = {
  nonceB64: string
  ciphertextB64: string
}

type SeedStore = {
  aesJwk: JsonWebKey
  wallets: Record<string, SeedRecord>
}

function keysFor(address: string): string[] {
  const addr = address.trim()
  if (!addr) return []
  const keys = [addr]
  for (const bounceable of [true, false]) {
    const form = addressToFriendlyForm(addr, bounceable)
    if (form && !keys.includes(form)) keys.push(form)
  }
  const raw = addressToRawForm(addr)
  if (raw && !keys.includes(raw)) keys.push(raw)
  return keys
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0)
  return btoa(binary)
}

function b64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

function readStore(): SeedStore | null {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SeedStore
    if (!parsed?.aesJwk || typeof parsed.wallets !== 'object' || !parsed.wallets) return null
    return parsed
  } catch {
    return null
  }
}

function writeStore(store: SeedStore): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(store))
}

async function aesKey(store: SeedStore, usages: KeyUsage[]): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', store.aesJwk, { name: 'AES-GCM' }, true, usages)
}

export async function persistDeviceWalletSeed(address: string, seedWords: string): Promise<void> {
  const seed = seedWords.trim()
  const addrs = keysFor(address)
  if (!seed || addrs.length === 0 || !globalThis.crypto?.subtle) return
  try {
    let store = readStore()
    if (!store) {
      const aes = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
        'encrypt',
        'decrypt',
      ])
      store = { aesJwk: await crypto.subtle.exportKey('jwk', aes), wallets: {} }
    }
    const key = await aesKey(store, ['encrypt'])
    const nonce = crypto.getRandomValues(new Uint8Array(12))
    const ciphertext = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce },
        key,
        toArrayBuffer(new TextEncoder().encode(seed))
      )
    )
    const record: SeedRecord = {
      nonceB64: bytesToB64(nonce),
      ciphertextB64: bytesToB64(ciphertext),
    }
    for (const addr of addrs) store.wallets[addr] = record
    writeStore(store)
  } catch {
    // device store is a convenience; trading still tries the vault
  }
}

export async function loadDeviceWalletSeed(address: string): Promise<string | null> {
  try {
    const store = readStore()
    if (!store) return null
    const key = await aesKey(store, ['decrypt'])
    const wanted = new Set(keysFor(address))
    const wantedRaw = addressToRawForm(address)
    const records: SeedRecord[] = []
    for (const [storedAddr, record] of Object.entries(store.wallets)) {
      if (!record) continue
      if (wanted.has(storedAddr) || (wantedRaw && addressToRawForm(storedAddr) === wantedRaw)) {
        records.push(record)
      }
    }
    for (const record of records) {
      const plain = new Uint8Array(
        await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: toArrayBuffer(b64ToBytes(record.nonceB64)) },
          key,
          toArrayBuffer(b64ToBytes(record.ciphertextB64))
        )
      )
      const seed = new TextDecoder().decode(plain)
      plain.fill(0)
      if (seed.trim()) return seed
    }
    return null
  } catch {
    return null
  }
}

export function resetDeviceWalletSeedsForTest(): void {
  try {
    localStorage.removeItem(STORE_KEY)
  } catch {
    // storage unavailable
  }
}
